import { useEffect, useState } from 'react';

const GLITCH_CHARS = 'ABCDEF0123456789{}<>/\\*#@';

type GlitchProps = {
  text: string;
  fontSize?: number;
  weight?: number;
  delay?: number;
  onComplete?: () => void;
};

export function GlitchText({
  text,
  fontSize = 56,
  weight = 700,
  delay = 0,
  onComplete,
}: GlitchProps) {
  const [shown, setShown] = useState('');
  const [glitching, setGlitching] = useState(true);
  const [ghostTick, setGhostTick] = useState(0);

  useEffect(() => {
    let i = 0;
    let timer: ReturnType<typeof setInterval> | null = null;
    const startTimer = setTimeout(() => {
      timer = setInterval(() => {
        i++;
        if (i > text.length) {
          if (timer) clearInterval(timer);
          setGlitching(false);
          onComplete?.();
        } else {
          setShown(text.slice(0, i));
        }
      }, 38);
    }, delay);
    return () => {
      clearTimeout(startTimer);
      if (timer) clearInterval(timer);
    };
  }, [text, delay, onComplete]);

  // ghostTick avanza solo mientras glitching → re-render del ghost cada 40ms
  useEffect(() => {
    if (!glitching) return;
    const id = setInterval(() => setGhostTick((x) => x + 1), 40);
    return () => clearInterval(id);
  }, [glitching]);

  const remaining = text.length - shown.length;
  const ghost = Array.from({ length: Math.min(remaining, 8) })
    .map(
      (_, i) =>
        GLITCH_CHARS[(ghostTick + i * 7) % GLITCH_CHARS.length],
    )
    .join('');

  return (
    <span
      style={{
        fontFamily: 'Montserrat, sans-serif',
        fontSize,
        fontWeight: weight,
        letterSpacing: '-0.035em',
        color: '#f6fbff',
        lineHeight: 1.05,
        textWrap: 'balance',
      }}
    >
      {shown}
      {glitching && (
        <span
          style={{
            color: 'rgba(102, 224, 255, 0.65)',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: fontSize * 0.65,
            fontWeight: 500,
            letterSpacing: 0,
            opacity: 0.7,
            marginLeft: 4,
          }}
        >
          {ghost}
        </span>
      )}
      {glitching && (
        <span
          style={{
            display: 'inline-block',
            width: '0.55em',
            height: '0.9em',
            background: '#66e0ff',
            marginLeft: 4,
            verticalAlign: 'baseline',
            animation: 'dd-blink-cursor 0.6s steps(1) infinite',
          }}
        />
      )}
    </span>
  );
}

export function EyebrowMono({
  text,
  delay = 0,
  color = 'rgba(102, 224, 255, 0.85)',
}: {
  text: string;
  delay?: number;
  color?: string;
}) {
  const [shown, setShown] = useState('');

  useEffect(() => {
    let i = 0;
    let timer: ReturnType<typeof setInterval> | null = null;
    const startTimer = setTimeout(() => {
      timer = setInterval(() => {
        i++;
        if (i > text.length) {
          if (timer) clearInterval(timer);
        } else {
          setShown(text.slice(0, i));
        }
      }, 24);
    }, delay);
    return () => {
      clearTimeout(startTimer);
      if (timer) clearInterval(timer);
    };
  }, [text, delay]);

  return (
    <span
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 11,
        fontWeight: 500,
        letterSpacing: '0.22em',
        color,
        textTransform: 'uppercase',
        display: 'inline-block',
      }}
    >
      {shown}
      <span style={{ opacity: shown.length < text.length ? 1 : 0 }}>▌</span>
    </span>
  );
}

export type BootLogLine = {
  tag: string;
  tone: 'ok' | 'warn' | 'err' | 'brand' | 'dim';
  msg: string;
};

export function BootLog({
  lines,
  startDelay = 0,
  lineDelay = 130,
}: {
  lines: BootLogLine[];
  startDelay?: number;
  lineDelay?: number;
}) {
  const [visible, setVisible] = useState(0);

  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(
      setTimeout(() => {
        lines.forEach((_, i) => {
          timers.push(setTimeout(() => setVisible(i + 1), i * lineDelay));
        });
      }, startDelay),
    );
    return () => timers.forEach(clearTimeout);
  }, [lines, startDelay, lineDelay]);

  const toneColor = (tone: BootLogLine['tone']) =>
    tone === 'ok'
      ? '#3ddc97'
      : tone === 'warn'
        ? '#f5b441'
        : tone === 'err'
          ? '#ff5e5e'
          : tone === 'brand'
            ? '#66e0ff'
            : 'rgba(255,255,255,0.55)';

  return (
    <div
      style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 10.5,
        lineHeight: 1.6,
        color: 'rgba(255,255,255,0.55)',
        letterSpacing: '0.02em',
      }}
    >
      {lines.slice(0, visible).map((l, i) => (
        <div key={i} className="dd-boot-line">
          <span style={{ color: 'rgba(102, 224, 255, 0.6)', marginRight: 8 }}>
            [{(i * 0.013).toFixed(3).padStart(7, '0')}]
          </span>
          <span style={{ color: toneColor(l.tone) }}>{l.tag}</span>
          <span style={{ marginLeft: 10 }}>{l.msg}</span>
        </div>
      ))}
    </div>
  );
}
